import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useMockAuth } from '../contexts/MockAuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { User, Building2, Chrome } from 'lucide-react';
import toast from 'react-hot-toast';
import { loginApi } from '../services/api';

// Toggle between real Firebase auth and mock auth
const USE_MOCK_AUTH = false;

const LoginPage = () => {
  const [selectedRole, setSelectedRole] = useState(null);
  const [loading, setLoading] = useState(false);
  const realAuth = useAuth();
  const mockAuth = useMockAuth();
  const { signInWithGoogle } = USE_MOCK_AUTH ? mockAuth : realAuth;
  const navigate = useNavigate();

  const handleRoleSelection = (role) => {
    setSelectedRole(role);
  };

  const handleGoogleSignIn = async () => {
    if (!selectedRole) {
      toast.error('Please select your role first');
      return;
    }

    setLoading(true);
    try {
      const authResult = await signInWithGoogle(selectedRole);
      const user = authResult?.user;
      if (!user) throw new Error('No user returned from SSO');

      // Persist minimal user and role immediately to avoid guard race
      try {
        sessionStorage.setItem(
          'user',
          JSON.stringify({ uid: user.uid, email: user.email, role: selectedRole })
        );
        sessionStorage.setItem('userRole', selectedRole);
      } catch {}

      // Node API login to obtain platform token
      try {
        const res = await loginApi({
          provide_by: 'GoogleLogin',
          provide_id: user.uid,
          email_id: user.email,
          name: user.displayName,
          profile_image: user.photoURL,
          role: selectedRole,
        });
        const token = res?.data?.data?.LoginResponse?.token;
        if (token) sessionStorage.setItem('token', token);
        console.log('nodeapi', res);
      } catch (apiErr) {
        console.warn('loginApi failed; continuing with Firebase session only', apiErr);
      }

      toast.success('Successfully signed in!');

      // When customer: prefill profile from displayName/email and auto-open profile popup on first arrival
      if (selectedRole === 'customer') {
        const dn = user.displayName || '';
        const parts = dn.trim().split(/\s+/);
        const firstName = parts[0] || '';
        const lastName = parts.slice(1).join(' ');
        try {
          sessionStorage.setItem('displayName', dn);
          sessionStorage.setItem('firstName', firstName);
          sessionStorage.setItem('lastName', lastName);
          sessionStorage.setItem('email', user.email || '');
          // Optional prefill placeholders (kept blank if not already set by earlier flows)
          if (!sessionStorage.getItem('mobile')) sessionStorage.setItem('mobile', '');
          if (!sessionStorage.getItem('dob')) sessionStorage.setItem('dob', '');
          if (!sessionStorage.getItem('address')) sessionStorage.setItem('address', '');
          // Request opening the profile popup on first visit
          sessionStorage.setItem('openProfileOnFirstVisit', 'true');
        } catch {}

        navigate('/customer/dashboard', { state: { openProfileOnFirstVisit: true } });
      } else {
        navigate('/business/dashboard');
      }
    } catch (error) {
      console.error('Sign in error:', error);
      toast.error('Failed to sign in. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Welcome to Rewards
          </h1>
          <p className="text-gray-600">
            Choose your role and sign in to get started
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 text-center">
              Select Your Role
            </h2>
            
            <div className="grid grid-cols-1 gap-4">
              {/* Customer Role */}
              <button
                onClick={() => handleRoleSelection('customer')}
                className={`p-6 rounded-xl border-2 transition-all duration-200 ${
                  selectedRole === 'customer'
                    ? 'border-blue-500 bg-blue-50 shadow-md'
                    : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                }`}
              >
                <div className="flex items-center space-x-4">
                  <div className={`p-3 rounded-full ${
                    selectedRole === 'customer' ? 'bg-blue-500' : 'bg-gray-400'
                  }`}>
                    <User className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-gray-900">Customer</h3>
                    <p className="text-sm text-gray-600">
                      Earn and redeem rewards from your favorite shops
                    </p>
                  </div>
                </div>
              </button>

              {/* Business Role */}
              <button
                onClick={() => handleRoleSelection('business')}
                className={`p-6 rounded-xl border-2 transition-all duration-200 ${
                  selectedRole === 'business'
                    ? 'border-green-500 bg-green-50 shadow-md'
                    : 'border-gray-200 hover:border-green-300 hover:bg-green-50'
                }`}
              >
                <div className="flex items-center space-x-4">
                  <div className={`p-3 rounded-full ${
                    selectedRole === 'business' ? 'bg-green-500' : 'bg-gray-400'
                  }`}>
                    <Building2 className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-gray-900">Business</h3>
                    <p className="text-sm text-gray-600">
                      Manage rewards and engage with your customers
                    </p>
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Google Sign In Button */}
          <button
            onClick={handleGoogleSignIn}
            disabled={!selectedRole || loading}
            className={`w-full flex items-center justify-center space-x-3 py-4 px-6 rounded-xl font-medium transition-all duration-200 ${
              selectedRole && !loading
                ? selectedRole === 'customer'
                  ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl'
                  : 'bg-green-600 hover:bg-green-700 text-white shadow-lg hover:shadow-xl'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Chrome className="w-5 h-5" />
            )}
            <span>
              {loading ? 'Signing in...' : 'Continue with Google'}
            </span>
          </button>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              By signing in, you agree to our Terms of Service and Privacy Policy
            </p>
          </div>
        </div>

        {/* Admin Portal quick access */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Are you an admin?{' '}
            <Link to="/admin" className="font-medium text-red-600 hover:text-red-700">
              Go to Admin Portal
            </Link>
          </p>
        </div>

        <div className="mt-8 text-center">
          <p className="text-sm text-gray-600">
            Need help? Contact our support team
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
